/**
 * Where you are in a room, as a URL — and the trail of where you have been.
 *
 * A stage change (Reports, Feed, a profile) is a place you went, so it lives
 * in the address: the browser's back button walks it, a refresh keeps it, and
 * a profile can be pasted to someone. A lens change (a tag, "confident only")
 * is the same place looked at differently, so it REPLACES the address rather
 * than pushing, and never adds a crumb. Pure, so jest can pin the round trip.
 */
import type { BoardroomReportKey } from '../../../types';
import { EMPTY_VIEW, type View, type VerifiedFilter } from '../filters';
import { LOOKUP_KINDS } from '../kinds';
import type { BoardroomItemKind } from '../../../types';
import { CARD_KINDS, REPORT_TABS, type Stage } from './reportsView';

export interface Place {
  stage: Stage;
  report: BoardroomReportKey;
  /** Which card, when `report` is one the chat built (`list` or `report`):
   *  two of those share a key, and the subject tells them apart. */
  subject?: string | null;
  profileId: number | null;
  view: View;
  /** On the board: which card is open, as `kind:subject`. In the address so
   *  Back from an item and a reload come back to it. */
  expanded?: string | null;
}

/** Report keys whose content is a card chosen by subject — the chat's tables
 *  and reports, and one company's brand searches. The same list the room and
 *  the tab strip use (`CARD_KINDS`): a second copy here left `brand` out, and
 *  "Open full report" landed on the first tab instead. */
export const CARD_REPORTS = CARD_KINDS as readonly BoardroomReportKey[];

const REPORT_KEYS = REPORT_TABS.map(t => t.key);
const VERIFIED: VerifiedFilter[] = ['HOLDS', 'DOES_NOT_HOLD', 'UNVERIFIABLE', 'UNCHECKED'];
const LOOKUPS = ['queued', 'fetching', 'landed', 'read', 'failed', 'not_fetched'] as const;

/** The report a room opens on: who is in this market is the first question. */
export const DEFAULT_REPORT: BoardroomReportKey = 'competitors';

export function placeFromSearch(search: string, fallbackStage: Stage): Place {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  // AN OPEN CARD NAMES ITS TABLE. A link with only `x` (typed, or shared
  // without the rest) fetched the default table instead, and the card said
  // "No report yet" for good. Read it as the full board address.
  const x = q.get('x') || '';
  if (x && !q.get('r') && !q.get('s') && (!q.get('v') || q.get('v') === 'board')) {
    const [xKind, ...xRest] = x.split(':');
    if (xKind !== DEFAULT_REPORT) q.set('r', xKind);
    if (xRest.join(':')) q.set('s', xRest.join(':'));
    q.set('v', 'board');
  }
  const v = q.get('v');
  const p = Number(q.get('p'));
  const profileId = Number.isInteger(p) && p > 0 ? p : null;
  const r = q.get('r') as BoardroomReportKey | null;
  // AN ADDRESS THAT NAMES A TABLE IS THAT TABLE. The board is where a room
  // opens (the bare address), but every saved link, crumb and History entry
  // from before named its table with `r` and no `v`; those still open it.
  const named = !!r || !!(q.get('s') || '').trim();
  const bare: Stage = named && fallbackStage === 'board' ? 'reports' : fallbackStage;
  let stage: Stage =
    v === 'feed' || v === 'reports' || v === 'board'
      ? v
      : v === 'profile' && profileId
        ? 'profile'
        : bare;
  if (stage === 'profile' && !profileId) stage = bare;
  const c = q.get('c') as VerifiedFilter | null;
  const u = q.get('u');
  const l = q.get('l') as View['lookup'];
  const k = q.get('k') as BoardroomItemKind | null;
  const s = (q.get('s') || '').trim();
  const table = !!r && REPORT_KEYS.includes(r);
  // A chat-built card, or a CUT: one of the tables with a filter of its own.
  // No `r` is the default table, left out of the address, so a cut of it too.
  const card = !!s && (!r || CARD_REPORTS.includes(r) || table);
  return {
    stage,
    report: r && (card || table) ? (r as BoardroomReportKey) : DEFAULT_REPORT,
    // Only when a card or a cut is open, so every other place is unchanged.
    ...(card ? { subject: s } : {}),
    ...(stage === 'board' && q.get('x') ? { expanded: q.get('x') } : {}),
    profileId,
    view: {
      filter: q.get('f') || null,
      tags: q.getAll('t'),
      verified: c && VERIFIED.includes(c) ? c : null,
      sourced: u === 'UNSOURCED' || u === 'SOURCED' ? u : null,
      by: q.get('b') || null,
      lookup: l && (LOOKUPS as readonly string[]).includes(l) ? l : null,
      kind: k && LOOKUP_KINDS.includes(k) ? k : null,
    },
  };
}

/** Defaults are left out, so a fresh room has a bare address. */
export function placeToSearch(place: Place): string {
  const q = new URLSearchParams();
  // THE BOARD IS WHERE A ROOM OPENS, so it is the address that says nothing,
  // and an address naming a table is that table (see `placeFromSearch`). So
  // `v` is written only where the rest of the address would read otherwise:
  // the board with a table named (the open card's), and the default table
  // with nothing named.
  const r = place.report !== DEFAULT_REPORT ? place.report : null;
  const s =
    place.subject && (CARD_REPORTS.includes(place.report) || REPORT_KEYS.includes(place.report))
      ? place.subject
      : null;
  const named = !!(r || s);
  if (place.stage === 'board' ? named : place.stage !== 'reports' || !named) q.set('v', place.stage);
  if (place.stage === 'profile' && place.profileId) q.set('p', String(place.profileId));
  if (r) q.set('r', r);
  if (s) q.set('s', s);
  if (place.stage === 'board' && place.expanded) q.set('x', place.expanded);
  const { filter, tags, verified, sourced, by, lookup, kind } = place.view;
  if (filter) q.set('f', filter);
  for (const t of tags) q.append('t', t);
  if (verified) q.set('c', verified);
  if (sourced) q.set('u', sourced);
  if (by) q.set('b', by);
  if (lookup) q.set('l', lookup);
  if (kind) q.set('k', kind);
  const out = q.toString();
  return out ? `?${out}` : '';
}

/** The lens: what narrows every table and the feed alike. */
export type Lens = Pick<View, 'tags' | 'verified' | 'sourced' | 'by'>;

export function lensOf(view: View): Lens {
  return { tags: view.tags, verified: view.verified, sourced: view.sourced, by: view.by };
}

export function sameLens(a: Lens, b: Lens): boolean {
  return (
    a.tags.length === b.tags.length &&
    a.tags.every((t, i) => t === b.tags[i]) &&
    a.verified === b.verified &&
    a.sourced === b.sourced &&
    a.by === b.by
  );
}

/** A saved address with today's lens on it. Pure.
 *
 *  THE LENS IS THE ROOM'S, not each place's. Back, the location bar and
 *  History go to addresses saved when you were last there, with the lens you
 *  had then, so setting a tag on one table and going back to another dropped
 *  it. Where you go comes from the saved address; how you look stays. */
export function withLens(search: string, lens: Lens, fallbackStage: Stage = 'board'): string {
  const p = placeFromSearch(search, fallbackStage);
  return placeToSearch({ ...p, view: { ...p.view, ...lens } });
}

export const EMPTY_PLACE: Place = { stage: 'board', report: DEFAULT_REPORT, profileId: null, view: EMPTY_VIEW };

/** What makes two places the same step: the table or item, not the lens on
 *  it. EACH TABLE IS ITS OWN STEP. Switching tables used to change the
 *  crumb's label in place, so Back skipped every table you had looked at and
 *  left the room view entirely. Now Back walks the tables too. */
export function crumbKey(place: Place): string {
  if (place.stage === 'profile') return `profile:${place.profileId}`;
  if (place.stage === 'reports') return `reports:${place.report}:${place.subject ?? ''}`;
  // A TABLE OPEN ON THE BOARD IS A PLACE YOU WENT. It used to be the same
  // step as the Dashboard, so opening one from an item cut the trail back to
  // an earlier board step and left Back with nowhere to go.
  if (place.stage === 'board') return place.expanded ? `board:${place.expanded}` : 'board';
  return 'feed';
}

export function placeLabel(place: Place, profileTitle?: string | null): string {
  if (place.stage === 'board') return 'Board';
  if (place.stage === 'feed') return 'Feed';
  if (place.stage === 'profile') return profileTitle || `#${place.profileId}`;
  return REPORT_TABS.find(t => t.key === place.report)?.label?.toString() ?? 'Reports';
}

export interface Crumb {
  key: string;
  label: string;
  /** The address at the time, lens included, so a crumb restores the view. */
  search: string;
}

export const TRAIL_MAX = 12;

/** The steps Back walks. Going somewhere you have been on this trail cuts the
 *  trail back to it; going somewhere new appends. The same place with a new
 *  lens updates in place.
 *
 *  The feed is never a step: it is a side panel now, and an old `v=feed`
 *  address is redirected to the tables at once. */
export function pushTrail(trail: Crumb[], crumb: Crumb): Crumb[] {
  if (crumb.key === 'feed') return trail;
  const at = trail.findIndex(c => c.key === crumb.key);
  if (at >= 0) return [...trail.slice(0, at), { ...trail[at], label: crumb.label, search: crumb.search }];
  return [...trail, crumb].slice(-TRAIL_MAX);
}

/** The crumb every trail starts from: the room's landing view. A profile you
 *  arrive at directly (a pasted link, a reload) still has somewhere to go back to. */
export function rootCrumb(fallbackStage: Stage): Crumb {
  // The board and the feed are roots you can arrive on; reports and a profile
  // are places you OPEN from one, so they come back to where they were opened.
  const stage: Stage = fallbackStage === 'feed' ? 'feed' : fallbackStage === 'reports' ? 'reports' : 'board';
  const place = { ...EMPTY_PLACE, stage };
  return { key: crumbKey(place), label: placeLabel(place), search: placeToSearch(place) };
}

/** Fold the current place into the trail. An item you arrive on directly (a
 *  pasted link, a reload) gets the room's landing table under it, so Back has
 *  somewhere to go; a table you arrive on is its own start. */
export function stepTrail(trail: Crumb[], crumb: Crumb, fallbackStage: Stage): Crumb[] {
  const base = trail.length === 0 && crumb.key.startsWith('profile:') ? [rootCrumb(fallbackStage)] : trail;
  return pushTrail(base, crumb);
}

// Versioned: a trail saved under an older crumb scheme is dropped, not merged.
// v5: the board became the bare address, so a v4 crumb's '' meant a table.
const TRAIL_STORE = 'jcseo.boardroom.trail.v5.';
const CRUMB_KEY_RE = /^(board(:.+)?|feed|reports:[a-z_]+:.*|profile:\d+)$/;

/** The trail for one room, kept for the tab's life so a reload keeps it.
 *  Only crumbs whose keys the current scheme understands survive, and a key
 *  never appears twice. */
export function loadTrail(boardroomId: number): Crumb[] {
  try {
    const raw = window.sessionStorage.getItem(TRAIL_STORE + boardroomId);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(parsed)) return [];
    const seen = new Set<string>();
    const out: Crumb[] = [];
    for (const c of parsed) {
      if (!c || typeof c.key !== 'string' || typeof c.search !== 'string' || !CRUMB_KEY_RE.test(c.key) || seen.has(c.key)) continue;
      seen.add(c.key);
      out.push({ key: c.key, label: typeof c.label === 'string' ? c.label : c.key, search: c.search });
    }
    return out;
  } catch {
    return [];
  }
}

export function saveTrail(boardroomId: number, trail: Crumb[]): void {
  try {
    window.sessionStorage.setItem(TRAIL_STORE + boardroomId, JSON.stringify(trail));
  } catch {
    /* private mode: the trail is per-visit anyway */
  }
}

// ---- History: every place opened in a room, newest first ----
//
// NOT THE TRAIL. The trail is a path back: revisiting a crumb cuts it short,
// and switching report tabs is not a step on it. History is a log: each
// company, product, report tab and card you open, in the order you opened
// them, kept across visits so you can find your way back to something you
// looked at yesterday.

export interface Visit {
  /** When it was opened, in ms. */
  at: number;
  /** What makes two visits the same place (a report tab is its own place). */
  key: string;
  label: string;
  /** What kind of thing it was: "company", "product", "report", "board"… */
  what: string;
  /** The address at the time, lens included. */
  search: string;
}

export const HISTORY_MAX = 200;

/** The history key: a profile by id, a report by tab and card. Pure. */
export function visitKey(place: Place): string {
  return crumbKey(place);
}

/** Add a visit, newest first. Opening the same place again with only the
 *  lens changed updates the newest entry rather than adding one; opening it
 *  again after going elsewhere is a new entry. Pure. */
export function pushVisit(list: Visit[], visit: Visit): Visit[] {
  const last = list[0];
  if (last && last.key === visit.key) {
    return [{ ...visit, label: visit.label || last.label, what: visit.what || last.what }, ...list.slice(1)];
  }
  return [visit, ...list].slice(0, HISTORY_MAX);
}

/** Give every visit to one place its proper name, once it is known. Pure. */
export function nameVisits(list: Visit[], key: string, label: string, what?: string): Visit[] {
  let changed = false;
  const out = list.map(v => {
    if (v.key !== key || (v.label === label && (!what || v.what === what))) return v;
    changed = true;
    return { ...v, label, what: what || v.what };
  });
  return changed ? out : list;
}

const HISTORY_STORE = 'jcseo.boardroom.history.v1.';

/** A room's history, kept in this browser across visits. */
export function loadHistory(boardroomId: number): Visit[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(HISTORY_STORE + boardroomId) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (v: Visit) =>
          v && typeof v.at === 'number' && typeof v.key === 'string' && typeof v.search === 'string',
      )
      .map((v: Visit) => ({ ...v, search: tableAddress(v.key, v.search) }))
      .slice(0, HISTORY_MAX);
  } catch {
    return [];
  }
}

/** A visit to a table saved before the board became the bare address: its
 *  address said nothing (the Competitors table), which now opens the board.
 *  Written the way it reads today. Pure. */
export function tableAddress(key: string, search: string): string {
  if (!key.startsWith('reports:') || placeFromSearch(search, 'board').stage === 'reports') return search;
  return placeToSearch({ ...placeFromSearch(search, 'reports'), stage: 'reports' });
}

export function saveHistory(boardroomId: number, list: Visit[]): void {
  try {
    window.localStorage.setItem(HISTORY_STORE + boardroomId, JSON.stringify(list));
  } catch {
    /* private mode or a full store: history is a convenience */
  }
}

/** Where the room's Back goes: the step before this one on the trail, and
 *  whether the browser's own Back lands there too. It does when this step was
 *  taken from that one (`from`, its crumb key, kept in the history entry's
 *  state). Otherwise - a reload, a pasted link, a step taken from somewhere the
 *  trail has since cut - the saved address is the way. Pure. */
export function backStep(trail: Crumb[], from: string | null | undefined): { crumb: Crumb; history: boolean } | null {
  if (trail.length < 2) return null;
  const crumb = trail[trail.length - 2];
  return { crumb, history: !!from && from === crumb.key };
}

/** The table an item was opened from, or null when there is none behind it.
 *  The location bar shows it between the room and the item. Pure.
 *
 *  Walks back past other items (a product opened from a feature stays under
 *  the table the feature was opened from) and STOPS at the first step that is
 *  not an item. That step counts when it is a full table, or the board with a
 *  card open - `card` is then that card, as `kind:subject`. Walking on past
 *  the board named a table from earlier in the tab, not the one you came from. */
export function tableBefore(trail: Crumb[]): (Crumb & { card?: string }) | null {
  for (let i = trail.length - 2; i >= 0; i -= 1) {
    const c = trail[i];
    if (c.key.startsWith('profile:')) continue;
    if (c.key.startsWith('reports:')) return c;
    if (c.key === 'board' || c.key.startsWith('board:')) {
      const card = placeFromSearch(c.search, 'board').expanded;
      return card ? { ...c, card } : null;
    }
    return null;
  }
  return null;
}
